import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmLeaveModal } from "modules/personalisation/components/ConfirmLeaveModal";

type PersonalisationLeaveContextValue = {
  readonly openConfirmLeave: () => void;
  readonly closeConfirmLeave: () => void;
};

const PersonalisationLeaveContext = createContext<PersonalisationLeaveContextValue | null>(null);

export function usePersonalisationLeave() {
  const ctx = useContext(PersonalisationLeaveContext);
  if (!ctx) throw new Error("usePersonalisationLeave must be used within provider");
  return ctx;
}

export function PersonalisationLeaveProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const openConfirmLeave = useCallback(() => setOpen(true), []);
  const closeConfirmLeave = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({ openConfirmLeave, closeConfirmLeave }),
    [openConfirmLeave, closeConfirmLeave],
  );

  return (
    <PersonalisationLeaveContext.Provider value={value}>
      {children}
      <ConfirmLeaveModal
        open={open}
        onContinue={closeConfirmLeave}
        onLeave={() => {
          setOpen(false);
          navigate("/home");
        }}
      />
    </PersonalisationLeaveContext.Provider>
  );
}
